/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 *
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.1.0
 */

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';


import {
    IPageTitleSettingButtonProps,
    renderDropFilterButton,
    renderSettingButton,
} from '@components/common/PageTitle/PageTitle';
import { dropTableFilterAndSort } from '@components/Table/actions/tableActions';
import { ITableField, ITableReducer, TableFieldType } from '@components/Table/reducers/tableReducer';
import {
    makeSelectorTableFields,
    makeSelectorTableFilter,
} from '@components/Table/selectors/tableSelectors';
import { IDropTableFilterAndSortPayload, TableNameType } from '@components/Table/types/tableTypes';
import { initTranslation } from '@modules/internationalization/constants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { IPageExpensesAnalyzerTranslation } from '@modules/internationalization/types';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { IRootReducer } from '@reducers/rootReducer';

import { icons } from '../../../../assets';
import { PageTitle } from '../../../../components/common';
import TableSetting from '../../../../components/TableSetting/TableSetting';
import DensityButton from '../../../../modules/density/components/DensityButton';
import ExportButton from '../../../../modules/exports/components/ExportButton';
import { expensesAnalyzerShowGraph } from '../../pageExpensesAnalyzerActions';
import { makeSelectorExpensesAnalyzerShowGraph } from '../../pageExpensesAnalyzerSelectors';
import { EExpensesAnalyzerField } from '../ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';

interface IAnalyzerTitleProps {
    sendTableFields: (tableFields: Array<ITableField<EExpensesAnalyzerField>>) => void;
    exportPath: string;
    translation: IPageExpensesAnalyzerTranslation;
}

interface IConnectedProps {
    fields: ITableReducer[TableNameType]['fields'];
    isShowGraph: boolean;
    selectedFilters: ITableReducer[TableNameType]['filter'];
}

interface IDispatchProps {
    dropFilterAndSort: (payload: IDropTableFilterAndSortPayload) => void;
    openModal: (payload: IModalOpenPayload) => void;
    showAnalyzerGraph: (payload: boolean) => void;
}

type TSettingTableField = ITableField<EExpensesAnalyzerField>;
type TExpensesAnalyzerTitle = IAnalyzerTitleProps & IConnectedProps & IDispatchProps;

const ExpensesAnalyzerTitle = (props: TExpensesAnalyzerTitle) => {

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { instructionsLinks: { expensesExport, expensesGeneral } } = translationCommon;

    /*
    * -------------------------------------------
    *           SHOW GRAPH
    * -------------------------------------------
    */

    const onShowGraphHandler = () => {
        props.showAnalyzerGraph(!props.isShowGraph);
    };

    /*
    * -------------------------------------------
    *           TABLE SETTINGS
    * -------------------------------------------
    */

    const fields = props.fields as TSettingTableField[];

    const filterFields = (fields: TSettingTableField[], type: TableFieldType[]): TSettingTableField[] =>
        fields.filter((value: TSettingTableField) => type.includes(value.type));

    const onTableSettingClickHandler = (): void => {
        props.openModal({
            render: (p: IModalRenderProps) =>
                <TableSetting
                    fields={filterFields(fields, ['S', 'M'])}
                    isOpen={false}
                    locale={({
                        table: props.translation.table,
                        title: props.translation.title.tableSetting,
                    })}
                    requiredFields={filterFields(fields, ['C'])}
                    onApply={(tableFields: Array<ITableField<EExpensesAnalyzerField>>) => {
                        props.sendTableFields(tableFields);
                        p.onCloseModal();
                    }}
                    onClose={p.onCloseModal}
                />,
        });
    };

    /*
    * -------------------------------------------
    *           BUTTONS CONFIG
    * -------------------------------------------
    */

    const ButtonsConfig: IPageTitleSettingButtonProps[] = [
        {
            dataName: 'show_graph',
            iconLeft: icons.chartIcon,
            id: 0,
            onClick: onShowGraphHandler,
            value: props.isShowGraph ? props.translation.title.hideGraph : props.translation.title.showGraph,
        },
        {
            dataName: 'Settings of columns',
            iconLeft: icons.settingIcon,
            id: 1,
            onClick: onTableSettingClickHandler,
            value: props.translation.title.settingColumn,
        },
        {
            component: <ExportButton
                instructionLink={expensesExport}
                src={props.exportPath}
                tableName={TableNameType.expensesAnalyzer}
            />,
            id: 2,
        },
        {
            id: 4,
            component: <DensityButton page={ECommonTranslationPages.bills}/>,
        },
    ];
    const isFilterSelected = Object.keys(props.selectedFilters).length !== 0;

    return (
        <PageTitle
            dataName="expensesAnalyzer"
            instructionLink={expensesGeneral}
            title={props.translation.title.expenses}
        >
            {
                // render drop filter button
                isFilterSelected && renderDropFilterButton({
                    onClick: () => props.dropFilterAndSort({ tableName: TableNameType.expensesAnalyzer }),
                    value: props.translation.title.dropFilters,
                })
            }
            {
                ButtonsConfig.map((button: IPageTitleSettingButtonProps) =>
                    button.component
                        ? <div className="page-title__button" key={button.id}>{button.component}</div>
                        : renderSettingButton(button),
                )
            }
        </PageTitle>
    );
};

ExpensesAnalyzerTitle.defaultProps = {
    translation: initTranslation.pageExpensesAnalyzer,
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    fields: makeSelectorTableFields(TableNameType.expensesAnalyzer),
    isShowGraph: makeSelectorExpensesAnalyzerShowGraph(),
    selectedFilters: makeSelectorTableFilter(TableNameType.expensesAnalyzer),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    dropFilterAndSort: (payload) => dispatch(dropTableFilterAndSort(payload)),
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    showAnalyzerGraph: (payload: boolean) => dispatch(expensesAnalyzerShowGraph(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpensesAnalyzerTitle);
